/**
 * Event Processors
 *
 * Processa gli eventi emessi dai sotto-componenti della sidebar (stripe, discount codes, ecc.)
 * Queste funzioni sono pure: analizzano l'evento e restituiscono le azioni da compiere,
 * senza modificare direttamente lo stato del componente.
 */

import { should_show_otp_block } from '../blocks-visibility'

/**
 * Processa gli eventi emessi dal componente Stripe
 *
 * Gestisce 3 tipi di eventi:
 * - **stripe-init**: reset dei dati carta
 * - **stripe-change**: nessuna azione (evento intermedio)
 * - **stripe-confirm**: salva dati carta e determina se mostrare/triggerare OTP
 *
 * @param {Object} event - Evento emesso da sidebar-stripe
 * @param {string} event.name - Nome evento ('stripe-init' | 'stripe-change' | 'stripe-confirm')
 * @param {Object} event.payload - Dati dell'evento
 * @param {boolean} event.payload.error - Se c'è stato un errore (solo stripe-confirm)
 * @param {string} event.payload.paymentIntentId - ID del payment intent Stripe
 * @param {string} event.payload.name - Nome intestatario carta
 * @param {Object} context - Contesto necessario per le decisioni
 * @param {boolean} context.otp_required - Se OTP è richiesto
 * @param {string|boolean} context.otp_check - Stato validazione OTP
 * @param {number} context.credit_card_amount - Importo carta di credito
 * @returns {import('../types').StripeProcessResult|null} - Oggetto con le azioni da compiere, null se nessuna azione
 */
export function process_stripe_event(event, context) {
  const { name, payload } = event
  const { otp_required, otp_check, credit_card_amount } = context

  // stripe-init: reset dati carta
  if (name === 'stripe-init') {
    return {
      cc_data: {},
      stripe_completed: false,
      should_show_otp: false,
      should_trigger_otp: false
    }
  }

  // stripe-change: nessuna azione (evento intermedio, il componente sta raccogliendo dati)
  if (name === 'stripe-change') {
    return null
  }

  // stripe-confirm: gestisce successo/errore del pagamento
  if (name === 'stripe-confirm') {
    // Errore: Stripe non completato
    if (payload.error) {
      return {
        cc_data: null,
        stripe_completed: false,
        should_show_otp: false,
        should_trigger_otp: false
      }
    }

    // Successo: salva dati e determina se mostrare OTP
    const stripe_completed_new = true
    const should_show_otp_new = should_show_otp_block({
      otp_required,
      otp_check,
      credit_card_amount,
      stripe_completed: stripe_completed_new
    })

    return {
      cc_data: {
        paymentIntentId: payload.paymentIntentId,
        nameSurname: payload.name
      },
      stripe_completed: stripe_completed_new,
      should_show_otp: should_show_otp_new,
      should_trigger_otp: should_show_otp_new, // trigger solo se deve essere mostrato
      stripe_block_payload: payload
    }
  }

  return null
}

/**
 * Estrae i dati dei codici sconto dall'evento emesso dal componente
 *
 * Semplice estrazione che normalizza la struttura dell'evento per il componente principale.
 *
 * @param {Object} event - Evento emesso da sidebar-discount-codes
 * @param {Object} event.payload - Payload dell'evento
 * @param {number} event.payload.discount_codes_amount - Totale codici sconto
 * @param {Array} event.payload.discount_codes - Array codici sconto
 * @returns {Object} - Oggetto con amount e codes
 * @returns {number} return.amount - Importo totale codici sconto
 * @returns {Array} return.codes - Array codici sconto
 */
export function extract_discount_codes_data(event) {
  return {
    amount: event.payload.discount_codes_amount,
    codes: event.payload.discount_codes
  }
}